/* Lesson reader — artboard 1c, rebuilt as full-bleed acts.
 *
 * The page is a sequence of bands rather than one column on one ground: the
 * eye needs somewhere to land when scanning a page this long, and a value
 * change across the full width is the cue that does it. Navigation moved to the
 * sticky breadcrumb (LessonNav), which is what makes full width possible.
 */

import s from './LessonReader.module.css';
import { RuleRow, MarginNote, Button } from '../design/primitives.jsx';
import { Blocks } from './LessonBody.jsx';
import Practice from './Practice.jsx';
import { inline } from '../content/inline.jsx';
import { unitName } from '../content/course.js';
import { useProgress } from '../lib/progress.jsx';
import { HookProvider } from '../lib/hook.jsx';
import { actsOf } from '../lib/sections.js';
import RepoPanel from './RepoPanel.jsx';

const cx = (...v) => v.filter(Boolean).join(' ');

const Act = ({ tone, children }) => (
  <section className={cx(s.act, s[tone])}><div className={s.actInner}>{children}</div></section>
);

export default function LessonReader({ lesson, unit, content, prev, next, onOpen, onOpenExtra }) {
  const { done, complete, enabled: progressOn } = useProgress();
  const margins = content?.margins || [];
  const isDone = !!done[lesson.id];
  const acts = content ? actsOf(content.body) : [];

  const continueLabel = next
    ? next.kind === 'trial' ? 'Continue to the trial →'
      : next.kind === 'check' ? 'Continue to the check →'
      : next.kind === 'assignment' ? 'Continue to the assignment →'
      : 'Continue →'
    : null;

  return (
    <HookProvider>
      <div className={s.reader}>
        <header className={s.head}>
          <RuleRow kicker={unit.meta ? `${unit.meta} · ${unitName(unit)}` : unitName(unit)} />   {/* no time estimate: Nick, 2026-10-03 — they serve no purpose */}
          <h1 className={s.title}>{lesson.title}</h1>
          {content?.lede && <p className={s.lede}>{inline(content.lede)}</p>}
        </header>

        {content?.kind === 'assignment' && <AssignmentRepo repo={content.repo} />}

        {!content && (
          <Act tone="read">
            <p className={s.empty}>
              This lesson has no body yet. Add{' '}
              <code>content/cs140/lessons/{lesson.id}.md</code>, run{' '}
              <code>npm run content</code>, and it will appear here.
            </p>
          </Act>
        )}

        {acts.map((act, i) => (
          <Act key={i} tone={act.tone}>
            {/* margin notes ride alongside the first reading act only */}
            {act.tone === 'read' && i === 1 && margins.length ? (
              <div className={cx(s.grid, s.withMargins)}>
                <div><Blocks blocks={act.blocks} /></div>
                <aside className={s.margins}>
                  {margins.map((m, j) => (
                    <MarginNote key={j} label={m.label} boxed={m.boxed}
                                entries={m.entries?.map(inline)}>{inline(m.body)}</MarginNote>
                  ))}
                </aside>
              </div>
            ) : (
              <div className={s.grid}><div><Blocks blocks={act.blocks} /></div></div>
            )}
          </Act>
        ))}

        {content?.practice && (
          <Act tone="panel">
            <div className={s.practiceCol}><Practice practice={content.practice} lessonId={lesson.id} /></div>
          </Act>
        )}

        <Act tone="read">
          {content?.extra && (
            <p className={s.extraLink}>
              <button type="button" onClick={() => onOpenExtra?.(lesson)}>
                Going further — {lesson.title}
              </button>
              <span> · optional, not assessed</span>
            </p>
          )}
          <div className={s.foot} style={{ marginTop: content?.extra ? 28 : 0 }}>
            {prev
              ? <button type="button" className={s.prev} onClick={() => onOpen?.(prev)}>← {prev.title}</button>
              : <span />}
            <div className={s.footActions}>
              {/* "Mark complete" only exists where completion is recorded
                  somewhere. With the flag off it is a button that changes
                  nothing, which is worse than no button. */}
              {progressOn && (
                <Button variant="secondary" onClick={() => complete(lesson.id)} disabled={isDone}>
                  {isDone ? '✓ Completed' : 'Mark complete'}
                </Button>
              )}
              {next && (
                <Button onClick={() => { complete(lesson.id); onOpen?.(next); }}>{continueLabel}</Button>
              )}
            </div>
          </div>
        </Act>
      </div>
    </HookProvider>
  );
}

/* The assignment's repository (D-002, D-039, D-051). Everything here is true
   for any reader: the template, the tests, a Codespace. No due date and no
   "Submit" — those are enrolment-bound, and live in Canvas. */
function AssignmentRepo({ repo }) {
  return (
    <Act tone="panel">
      <div className={s.grid}><div>
        {repo.published
          ? <RepoPanel org={repo.org} name={repo.name} studentsOrg={repo.studentsOrg} />
          : <p className={s.repoLinks}><em>The template repository is not published yet.</em></p>}
      </div></div>
    </Act>
  );
}

